import { ArrowTopRightOnSquareIcon, DevicePhoneMobileIcon, ExclamationTriangleIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import { QRCodeSVG } from 'qrcode.react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

const MOBILE_VIEWPORT_QUERY = '(max-width: 639px)';

const isHttpUrl = (value) => {
  if (typeof value !== 'string') {
    return false;
  }

  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

const getIsMobileViewport = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia(MOBILE_VIEWPORT_QUERY).matches;
};

const useIsMobileViewport = () => {
  const [isMobileViewport, setIsMobileViewport] = useState(getIsMobileViewport);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_VIEWPORT_QUERY);
    const handleChange = () => setIsMobileViewport(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isMobileViewport;
};

export default function DownloadQrCode({
  url,
  label = 'QR Code de download',
  description = 'Escaneie para abrir o download no dispositivo Android.',
  loading = false,
  error = false,
  framed = true,
  className,
}) {
  const normalizedUrl = isHttpUrl(url) ? url.trim() : null;
  const isMobileViewport = useIsMobileViewport();

  return (
    <div className={clsx(framed && 'rounded-2xl border border-slate-200 bg-white p-4 shadow-sm', !framed && 'p-0', className)}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        {isMobileViewport ? (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
            {error ? (
              <ExclamationTriangleIcon className="h-6 w-6 text-rose-500" aria-hidden="true" />
            ) : (
              <DevicePhoneMobileIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
            )}
          </div>
        ) : (
          <div className="flex h-36 w-36 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 p-3">
            {loading ? (
              <div className="h-full w-full animate-pulse rounded-xl bg-slate-200" aria-hidden="true" />
            ) : error ? (
              <ExclamationTriangleIcon className="h-10 w-10 text-rose-500" aria-hidden="true" />
            ) : normalizedUrl ? (
              <QRCodeSVG
                value={normalizedUrl}
                size={128}
                level="M"
                includeMargin
                bgColor="#ffffff"
                fgColor="#0f172a"
                title={label}
              />
            ) : (
              <QrCodeIcon className="h-10 w-10 text-slate-400" aria-hidden="true" />
            )}
          </div>
        )}

        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-950">{isMobileViewport ? 'Download no dispositivo' : label}</p>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            {loading
              ? 'Carregando link de download autorizado.'
              : error
                ? isMobileViewport
                  ? 'Não foi possível carregar o link de download agora. Tente novamente em instantes.'
                  : 'Não foi possível carregar o QR Code agora. Use o botão de download ou tente novamente em instantes.'
                : normalizedUrl
                  ? isMobileViewport
                    ? 'Abra o download diretamente neste dispositivo Android.'
                    : description
                  : isMobileViewport
                    ? 'Nenhum link de download está disponível no momento.'
                    : 'Nenhum link de download está disponível para gerar o QR Code.'}
          </p>
          {normalizedUrl && !loading && !error ? (
            isMobileViewport ? (
              <a
                href={normalizedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Abrir download
                <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
              </a>
            ) : (
              <p className="mt-2 break-all text-xs leading-5 text-slate-500">{normalizedUrl}</p>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}
